// Shirts are rated one at a time and each rating auto-advances to the next shirt.
// Every rating is saved to the backend as soon as it is tapped.
const API_BASE_URL = 'https://shopify-store-omega.vercel.app';

class PagedRatings {
  constructor(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    this.setId = root.dataset.setId || 'A';
    this.batchLinks = (root.dataset.batchLinks || '').split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
      const [id, url, name] = l.split('|').map((x) => x.trim());
      return id && url ? { id, url, name: name || '' } : null;
    }).filter(Boolean);
    this.version = root.dataset.batchVersion || '1';
    this.discountCode = root.dataset.discountCode || 'RATE10';
    this.ratings = {};
    this.index = 0;
    this.titles = {};
    this.inFlight = false;
    this.dirty = false;
    this.retries = 0;
    this.campaign = this.resolveCampaign();
    this.k = (name) => `ratings_paged_${name}_${this.id}`;

    this.checkVersion();
    this.shuffleDeck();
    this.cacheDom();
    this.loadRatings();
    this.bind();
    this.retryPendingComment();
    this.paintAllStars();
    this.index = this.firstUnratedIndex();
    if (this.isAllRated()) {
      this.commentDone() ? this.showReward() : this.showComment();
    } else {
      this.showCard(this.index, { scroll: false });
    }
  }

  q(sel, scope = this.root) { return scope.querySelector(sel); }
  qa(sel, scope = this.root) { return Array.from(scope.querySelectorAll(sel)); }

  // Random shirt order, reshuffled on every page load. Shirts the visitor already rated stay at the
  // front (in the order they rated them) so a refresh never shows them a rated shirt again;
  // only the remaining shirts are shuffled.
  shuffleDeck() {
    const deck = this.q('.rp-deck');
    const nav = this.q('.rp-nav', deck);
    const cards = this.qa('.rp-card', deck);
    const ids = cards.map((c) => c.dataset.productId);

    let rated = {};
    try { rated = JSON.parse(localStorage.getItem(this.k('ratings')) || '{}') || {}; } catch (e) { rated = {}; }
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(this.k('order')) || '[]'); } catch (e) { saved = []; }
    if (!Array.isArray(saved)) saved = [];

    const done = saved.filter((id) => ids.includes(id) && rated[id] > 0);
    const rest = ids.filter((id) => !done.includes(id) && !(rated[id] > 0));
    const doneExtra = ids.filter((id) => !done.includes(id) && rated[id] > 0);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    const sequence = done.concat(doneExtra, rest);
    try { localStorage.setItem(this.k('order'), JSON.stringify(sequence)); } catch (e) { /* ignore */ }
    sequence.forEach((id) => deck.insertBefore(cards.find((c) => c.dataset.productId === id), nav));

    // The first two shirts are what people see first: load them eagerly
    this.qa('.rp-card img', deck).forEach((img, i) => { img.loading = i < 2 ? 'eager' : 'lazy'; });
  }

  cacheDom() {
    this.cards = this.qa('.rp-card');
    this.total = this.cards.length;
    this.cards.forEach((c) => {
      this.titles[c.dataset.productId] = this.q('.rp-name', c).textContent.trim();
    });
    this.status = this.q(`#rp-status-${this.id}`);
    this.barFill = this.q(`#rp-bar-fill-${this.id}`);
    this.backBtn = this.q(`#rp-back-${this.id}`);
    this.container = this.q(`#rating-container-${this.id}`);
    this.success = this.q(`#success-container-${this.id}`);
    this.comment = this.q(`#rp-comment-${this.id}`);
  }

  resolveCampaign() {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('c');
      if (fromUrl) {
        localStorage.setItem('ratings_campaign', fromUrl.slice(0, 32));
        return fromUrl.slice(0, 32);
      }
      return localStorage.getItem('ratings_campaign');
    } catch (e) {
      return null;
    }
  }

  getSessionId() {
    // One session per cohort so a person doing two sets doesn't overwrite their own submission row
    const key = `ratings_paged_session_id_${this.setId}`;
    try {
      let sid = localStorage.getItem(key);
      if (!sid) {
        sid = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        localStorage.setItem(key, sid);
      }
      return sid;
    } catch (e) {
      this.memSid = this.memSid || `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      return this.memSid;
    }
  }

  checkVersion() {
    try {
      if (localStorage.getItem(this.k('version')) !== this.version) {
        localStorage.removeItem(this.k('ratings'));
        localStorage.removeItem(this.k('order'));
        localStorage.removeItem(this.k('comment_done'));
        localStorage.removeItem(this.k('comment_pending'));
        localStorage.removeItem(`ratings_paged_session_id_${this.setId}`);
        localStorage.setItem(this.k('version'), this.version);
      }
    } catch (e) { /* storage unavailable: start fresh each visit */ }
  }

  loadRatings() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.k('ratings')) || '{}');
      if (saved && typeof saved === 'object') this.ratings = saved;
    } catch (e) { this.ratings = {}; }
  }

  persist() {
    try { localStorage.setItem(this.k('ratings'), JSON.stringify(this.ratings)); } catch (e) { /* ignore */ }
  }

  bind() {
    this.cards.forEach((card) => {
      const stars = this.qa('.rp-star', card);
      const group = this.q('.rp-stars', card);
      stars.forEach((btn) => {
        const value = parseInt(btn.dataset.rating, 10);
        btn.addEventListener('click', () => this.rate(card, value));
        // Mouse hover preview: fill every star up to the one under the pointer
        btn.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          group.classList.add('is-previewing');
          stars.forEach((s) => s.classList.toggle('is-hover', parseInt(s.dataset.rating, 10) <= value));
        });
      });
      group.addEventListener('pointerleave', () => {
        group.classList.remove('is-previewing');
        stars.forEach((s) => s.classList.remove('is-hover'));
      });
    });

    this.backBtn.addEventListener('click', () => this.showCard(this.index - 1));

    // Flush anything unsent when the customer leaves
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.dirty) this.save(true);
    });
    window.addEventListener('pagehide', () => { if (this.dirty) this.save(true); });

    const input = this.q(`#rp-comment-input-${this.id}`);
    const count = this.q(`#rp-comment-count-${this.id}`);
    input.addEventListener('input', () => { count.textContent = input.value.length; });
    this.q(`#rp-comment-submit-${this.id}`).addEventListener('click', () => this.submitComment(input.value.trim()));
    this.q(`#rp-comment-skip-${this.id}`).addEventListener('click', () => this.finish());

    const copyBtn = this.q(`#copy-code-${this.id}`);
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const label = this.q('span', copyBtn);
        navigator.clipboard.writeText(this.discountCode).then(() => {
          label.textContent = 'COPIED!';
          setTimeout(() => { label.textContent = 'COPY CODE'; }, 2000);
        }).catch(() => {
          label.textContent = this.discountCode;
        });
      });
    }

    const applyBtn = this.q(`#apply-discount-${this.id}`);
    if (applyBtn) {
      applyBtn.addEventListener('click', async () => {
        const label = applyBtn.textContent;
        applyBtn.textContent = 'Applying...';
        applyBtn.disabled = true;
        try {
          const res = await fetch(`/discount/${encodeURIComponent(this.discountCode)}`);
          if (!res.ok) throw new Error('discount failed');
          applyBtn.textContent = 'Applied! Redirecting...';
          setTimeout(() => { window.location.href = '/'; }, 1000);
        } catch (e) {
          applyBtn.textContent = 'Copy the code and apply it at checkout';
          setTimeout(() => { applyBtn.textContent = label; applyBtn.disabled = false; }, 3000);
        }
      });
    }

    const edit = this.q(`#edit-ratings-${this.id}`);
    if (edit) {
      edit.addEventListener('click', (e) => {
        e.preventDefault();
        this.success.style.display = 'none';
        this.comment.style.display = 'none';
        this.container.style.display = 'block';
        this.index = 0;
        this.showCard(0);
      });
    }
  }

  // ---- ratings ----

  rate(card, value) {
    const pid = card.dataset.productId;
    this.ratings[pid] = value;
    this.persist();
    this.paintStars(card, value);
    card.classList.remove('is-saved'); // flips to "Saved" once the backend confirms
    this.save();

    // Move on to the next shirt (or the comment step after the last one)
    clearTimeout(this.advanceTimer);
    this.advanceTimer = setTimeout(() => {
      if (this.index < this.total - 1) {
        this.showCard(this.index + 1);
      } else if (this.isAllRated()) {
        this.showComment();
      }
    }, 350);
  }

  paintStars(card, value) {
    this.qa('.rp-star', card).forEach((btn) => {
      const on = parseInt(btn.dataset.rating, 10) <= value;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on && parseInt(btn.dataset.rating, 10) === value ? 'true' : 'false');
    });
  }

  paintAllStars() {
    this.cards.forEach((card) => {
      const v = this.ratings[card.dataset.productId] || 0;
      this.paintStars(card, v);
      if (v) card.classList.add('is-saved');
    });
  }

  // Sends the full ratings map so far. The backend upserts per (session, product)
  // but overwrites the submissions row, so a partial payload would drop earlier ratings.
  async save(keepalive = false) {
    this.dirty = true;
    if (this.inFlight && !keepalive) return;
    this.inFlight = true;
    this.dirty = false;
    const sent = { ...this.ratings };

    try {
      const res = await fetch(`${API_BASE_URL}/api/ratings/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive,
        body: JSON.stringify({
          ratings: sent,
          sessionId: this.getSessionId(),
          campaign: this.campaign,
          metadata: {
            setId: this.setId,
            productTitles: this.titles,
            pageUrl: window.location.href,
            campaign: this.campaign
          }
        })
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      this.retries = 0;
      Object.keys(sent).forEach((pid) => {
        if (this.ratings[pid] !== sent[pid]) return; // changed again mid-flight; next save confirms it
        const card = this.q(`.rp-card[data-product-id="${pid}"]`);
        if (card) card.classList.add('is-saved');
      });
    } catch (e) {
      this.dirty = true;
      this.retries += 1;
    } finally {
      this.inFlight = false;
    }

    if (this.dirty && !keepalive) {
      if (this.retries === 0) {
        this.save(); // a newer tap arrived while this request was in flight
      } else if (this.retries <= 4) {
        setTimeout(() => this.save(), 1500 * this.retries);
      }
    }
  }

  // ---- shirts ----

  ratedCount() {
    return this.cards.filter((c) => this.ratings[c.dataset.productId] > 0).length;
  }

  isAllRated() {
    return this.ratedCount() === this.total;
  }

  firstUnratedIndex() {
    const i = this.cards.findIndex((c) => !(this.ratings[c.dataset.productId] > 0));
    return i === -1 ? this.total - 1 : i;
  }

  showCard(i, { scroll = true } = {}) {
    if (i < 0 || i >= this.total) return;
    this.index = i;
    this.cards.forEach((c, n) => c.classList.toggle('is-active', n === i));

    // Preload the next shirt so the swap feels instant
    const next = this.cards[i + 1];
    const nextImg = next && this.q('img', next);
    if (nextImg) nextImg.loading = 'eager';

    this.container.style.display = 'block';
    this.comment.style.display = 'none';
    this.refreshProgress();
    if (scroll) this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  refreshProgress() {
    this.status.textContent = `${this.index + 1} of ${this.total}`;
    this.barFill.style.width = `${((this.index + 1) / this.total) * 100}%`;
    this.backBtn.hidden = this.index === 0;
  }

  retryPendingComment() {
    let text = null;
    try { text = localStorage.getItem(this.k('comment_pending')); } catch (e) { return; }
    if (!text) return;
    this.postComment(text).then((ok) => {
      if (ok) { try { localStorage.removeItem(this.k('comment_pending')); } catch (e) { /* ignore */ } }
    });
  }

  commentDone() {
    try { return localStorage.getItem(this.k('comment_done')) === '1'; } catch (e) { return false; }
  }

  markCommentDone() {
    try { localStorage.setItem(this.k('comment_done'), '1'); } catch (e) { /* ignore */ }
  }

  showComment() {
    // The ratings are done once the comment step is reached; the comment itself is optional
    this.markBatchCompleted();
    this.renderAnotherBatch();
    this.container.style.display = 'none';
    const submit = this.q(`#rp-comment-submit-${this.id}`);
    submit.disabled = false;
    submit.textContent = 'Unlock my reward';
    this.comment.style.display = 'block';
    this.comment.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // The reward never depends on the comment: a failed save is retried in the background.
  async submitComment(text) {
    if (!text) { this.finish(); return; }
    const btn = this.q(`#rp-comment-submit-${this.id}`);
    const err = this.q(`#rp-comment-error-${this.id}`);
    btn.disabled = true;
    btn.textContent = 'Saving...';
    err.hidden = true;
    const ok = await this.postComment(text);
    if (!ok) {
      try { localStorage.setItem(this.k('comment_pending'), text); } catch (e) { /* ignore */ }
    }
    this.finish();
  }

  async postComment(text) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ratings/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.getSessionId(),
          comment: text,
          setId: this.setId,
          campaign: this.campaign,
          pageUrl: window.location.href
        })
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  finish() {
    this.markCommentDone();
    this.comment.style.display = 'none';
    this.showReward();
  }

  // Completed batches are shared across the cohort pages (same origin), keyed by set + version
  completedBatches() {
    try { return JSON.parse(localStorage.getItem('ratings_paged_completed') || '[]') || []; } catch (e) { return []; }
  }

  markBatchCompleted() {
    const key = `${this.setId}@${this.version}`;
    const done = this.completedBatches();
    if (done.includes(key)) return;
    done.push(key);
    try { localStorage.setItem('ratings_paged_completed', JSON.stringify(done)); } catch (e) { /* ignore */ }
  }

  // "Rate another batch": next listed batch not finished yet; hidden once every batch is done
  // Lists every other batch the customer hasn't finished yet (reward and comment steps);
  // hides the lists once all are done. Opens a batch in place, without a full page load.
  renderAnotherBatch() {
    const boxes = this.qa('.rp-more');
    if (!boxes.length || !this.batchLinks.length) return;
    const done = this.completedBatches();
    const pending = this.batchLinks.filter((b) => b.id !== this.setId && !done.includes(`${b.id}@${this.version}`));

    const at = this.batchLinks.findIndex((b) => b.id === this.setId);
    const ordered = this.batchLinks.slice(at + 1).concat(this.batchLinks.slice(0, Math.max(at, 0)));
    const items = ordered.filter((b) => pending.includes(b));

    boxes.forEach((box) => {
      const list = this.q('.rp-more-list', box);
      list.textContent = '';
      box.hidden = !items.length;
      items.forEach((b) => {
        let url = b.url;
        if (this.campaign) url += (url.includes('?') ? '&' : '?') + 'c=' + encodeURIComponent(this.campaign);
        const a = document.createElement('a');
        a.className = 'rp-more-item';
        a.href = url;
        const name = document.createElement('span');
        name.textContent = b.name || `Batch ${b.id}`;
        const arrow = document.createElement('span');
        arrow.className = 'rp-more-arrow';
        arrow.textContent = '\u2192';
        a.append(name, arrow);
        a.addEventListener('click', (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
          e.preventDefault();
          this.openBatch(url);
        });
        list.appendChild(a);
      });
    });
  }

  // Swap this page's survey for another batch's survey without reloading the page.
  // Any failure falls back to a normal page load.
  async openBatch(url) {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('fetch failed');
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const nextWrap = doc.querySelector('.ratings-paged') && doc.querySelector('.ratings-paged').closest('.shopify-section');
      const wrap = this.root.closest('.shopify-section');
      if (!nextWrap || !wrap) throw new Error('survey not found');

      // Make sure the ratings given so far are on their way before the old survey goes away
      if (this.dirty) this.save(true);

      wrap.innerHTML = nextWrap.innerHTML;
      history.pushState({ ratingsBatch: true }, '', url);
      if (doc.title) document.title = doc.title;
      if (!window.__rpPopstate) {
        window.__rpPopstate = true;
        window.addEventListener('popstate', () => window.location.reload());
      }
      initPagedRatings();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      window.location.href = url;
    }
  }

  showReward() {
    this.markBatchCompleted();
    this.renderAnotherBatch();
    this.container.style.display = 'none';
    this.success.style.display = 'block';
    this.success.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function initPagedRatings() {
  document.querySelectorAll('.ratings-paged').forEach((el) => {
    if (el.dataset.initialized === 'true') return;
    el.dataset.initialized = 'true';
    new PagedRatings(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPagedRatings);
} else {
  initPagedRatings();
}
document.addEventListener('shopify:section:load', initPagedRatings);
